// PreviewOverlay — キャンバス全体プレビュー (旧版 `PreviewCanvas.java` 踏襲).
//
// 旧版挙動: 画面右下に小さな子 dialog (`PreviewFrame`) が開き、その中の
// `PreviewCanvas` が main canvas の縮小画像を再描画 + 表示範囲を黒枠で重ね、
// ドラッグでスクロール位置を動かせた. 新版は ColorWheelPalette と同流儀の
// floating panel (= `position: fixed` + drag handle + × ボタン) として実装.
//
// 設計の核心:
// - main canvas (`canvasRef`) を `drawImage(main, 0, 0, thumbW, thumbH)` で
//   縮小描画. zoom は描画には影響しない (= サムネは常に native canvas を映す).
// - 表示範囲枠の x/y/w/h と、クリック → wrapper.scrollLeft/Top 変換は
//   `previewOverlay.ts` の pure helper に集約 (node vitest 対象).
// - 自動 redraw: requestAnimationFrame ループ. cheap な copy なので 1 frame
//   毎に main → thumbnail に焼き直すだけで, 「描画したストロークが即サムネに
//   反映される」(= 旧版踏襲、`whiteboardCanvas.getBufferImage()` を直接読む).
// - 表示範囲枠も毎 frame 同時に再描画 (scroll 位置が動いた瞬間に枠が追従).
// - クリック/ドラッグ: 旧版踏襲で「クリック点を viewport の **中心** に持って
//   いく」挙動. `previewClickToScroll` で計算した scrollLeft/Top を wrapper に
//   代入. setPointerCapture でドラッグ中に枠外に出ても継続追従.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import {
  type CanvasViewMapper,
  measureSlot,
  type ViewportOffset,
} from '../../../infrastructure/drawing/viewport.js';
import { canvasBackgroundStyle } from '../shared/canvasBackgroundStyle.js';
import { usePreviewThumbnailLoop } from '../shared/usePreviewThumbnailLoop.js';
import {
  clampPopupPosition,
  type FloatingDragAnchor,
  type FloatingPopupPosition,
  nextPositionFromDrag,
} from './floatingChatPopup.js';
import { outsetFrame } from './legacyFrames.js';
import {
  clampPreviewEdgeToViewport,
  computePreviewThumbnailSize,
  DEFAULT_PREVIEW_EDGE,
  effectivePreviewMaxEdge,
  nextPreviewResize,
  type PreviewResizeZone,
  previewClickToViewport,
  previewClickToViewportRotated,
} from './previewOverlay.js';

export interface PreviewOverlayProps {
  /** Main 描画 canvas. drawImage source 用. mount 後に確定 (StrictMode で再 mount される可能性あり). */
  readonly canvasRef: React.RefObject<HTMLCanvasElement | null>;
  /**
   * Canvas 表示枠 (= `overflow: hidden` の `<main>`). `clientWidth/Height`
   * (= 表示範囲サイズの近似) を読むだけ — 2026-09-29 viewport transform 移行
   * (決定5) 以降、pan 位置は `viewportRef`/`onSetViewport` 経由になった。
   */
  readonly wrapperRef: React.RefObject<HTMLDivElement | null>;
  /**
   * 表示変換の窓口 (= `useRoomChrome.viewMapper`)。表示範囲枠 (回転中は傾いた四角形、
   * canvas-rotation 段取り 2) の描画とサムネ click の逆算に使う。
   */
  readonly viewMapper: CanvasViewMapper;
  /**
   * pan オフセットの書き込み (= `useRoomChrome.setViewport`)。サムネ click/drag
   * で呼ぶ。CanvasStage 側の DOM transform 適用までこの 1 関数が担う (= 呼び出し
   * 側は DOM に触れない)。
   */
  readonly onSetViewport: (next: ViewportOffset) => void;
  /** Main canvas の native px (= zoom 適用前). サムネ寸法計算に使う. */
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  /** 現在の zoom (1..16). viewport 矩形計算 + click → scroll 変換に使う. */
  readonly zoom: number;
  /**
   * canvas が左右反転表示中か (= ToolPanel「反転」/「V」キー). engine は canvas
   * 要素に `scaleX(-zoom)` を当てて反転するため、同じ scrollLeft でも実際に
   * 見えている真の canvas 領域は左右ミラーになる. サムネ自体は鏡像化せず
   * (CSP 流 / Option A)、表示範囲枠と click→scroll の X だけを thumb 幅で反射
   * して整合させる (= `previewOverlay.ts` の flipX 引数へ渡すだけ).
   */
  readonly flipX: boolean;
  /** × ボタン / ESC で閉じる. caller は previewOpen=false に倒す. */
  readonly onClose: () => void;
  /**
   * 初期サムネ最大辺 (px). caller (ClassicLayout) が所有する
   * `PreviewSizePreferenceStore.get()` の永続値を seed として渡す.
   * 省略時は `DEFAULT_PREVIEW_EDGE` (= 240) で従来挙動.
   */
  readonly initialEdge?: number;
  /**
   * リサイズ確定時 (pointerup) のコールバック. caller は
   * `store.set(edge)` で永続化する. move 毎でなく確定時 1 回だけ呼ぶ
   * (= localStorage 過剰書き込みを避ける).
   */
  readonly onResizeCommit?: (edge: number) => void;
  /**
   * 2026-06-13 (feature/preview-perf): 「自分のローカル操作中か」を返す getter。
   * `true` のときサムネ再生成をサスペンドする (= 表示範囲枠だけは追従)。caller
   * (ClassicLayout) が `chrome.isStrokeActive()` (= 自分の描画中) + パン中を合成する。
   * 未指定なら常に false (= 低レートスロットルのみ、操作中サスペンドなし)。
   */
  readonly isSuspended?: () => boolean;
  /**
   * 2026-09-28 (feature/preview-panel-background): 部屋の背景可視/背景色。メイン
   * canvas (`DrawingCanvas`) は engine が透明に描き、背景色は CSS の inline
   * `background-color` で敷く方式 (2026-06-20 canvas-background-color)。プレビューは
   * main canvas を `drawImage` で縮小コピーするだけなので CSS 背景は写らない —
   * サムネ canvas 自体にも同じ CSS 背景 (可視: `backgroundRgb` / 非可視:
   * `canvas-bg-checker` 市松) を当てて解消する (`../shared/canvasBackgroundStyle.js`、
   * DrawingCanvas と同じ helper)。caller (ClassicLayout) が `chrome.backgroundVisible` /
   * `chrome.backgroundRgb` をそのまま渡す。未指定時は DrawingCanvas 側の既定 (可視・白)
   * と同じ挙動になる。
   */
  readonly backgroundVisible?: boolean;
  readonly backgroundRgb?: number;
}

/** 透明リサイズゾーンの厚み (辺ストリップ) / 隅正方形のサイズ (px). */
const EDGE_ZONE_THICKNESS = 6;
const CORNER_ZONE_SIZE = 12;

/**
 * 8 方向リサイズゾーン (4 辺 + 4 隅) の静的定義 (= JSX で map して描く).
 *
 * 見えるハッチ掴み代は撤去し、OS ウィンドウ風に「パネルの縁に乗る透明な判定
 * ゾーン」をホバーで resize カーソルに変えてドラッグさせる (= 意匠は出さない).
 *
 * - `style`: パネル直下 (`position:absolute`) での縁への寄せ. `inset` を負値
 *   気味にして枠の上に乗せ「枠を掴む」感を出す. 辺は細いストリップ、隅は小
 *   正方形. 隅は辺より手前 (z 高) に重ねて角で対角リサイズが効くようにする.
 * - `cursor`: n/s=ns / e/w=ew / ne・sw=nesw / nw・se=nwse.
 * - `z`: 辺=20, 隅=21. n/ne/nw はタイトルバーに重なるので、いずれも
 *   タイトルバー (= 既定の通常フロー) より手前に置き stopPropagation で
 *   drag-move と分離する (= Windows の「最上端の細い帯 = リサイズ」を再現).
 */
const HALF_EDGE = EDGE_ZONE_THICKNESS / 2;
const PREVIEW_ZONES: ReadonlyArray<{
  readonly zone: PreviewResizeZone;
  readonly cursor: 'ns-resize' | 'ew-resize' | 'nesw-resize' | 'nwse-resize';
  readonly z: number;
  readonly style: React.CSSProperties;
}> = [
  // ── 辺ストリップ (z=20) ──
  {
    zone: 'n',
    cursor: 'ns-resize',
    z: 20,
    style: {
      top: -HALF_EDGE,
      left: CORNER_ZONE_SIZE,
      right: CORNER_ZONE_SIZE,
      height: EDGE_ZONE_THICKNESS,
    },
  },
  {
    zone: 's',
    cursor: 'ns-resize',
    z: 20,
    style: {
      bottom: -HALF_EDGE,
      left: CORNER_ZONE_SIZE,
      right: CORNER_ZONE_SIZE,
      height: EDGE_ZONE_THICKNESS,
    },
  },
  {
    zone: 'e',
    cursor: 'ew-resize',
    z: 20,
    style: {
      right: -HALF_EDGE,
      top: CORNER_ZONE_SIZE,
      bottom: CORNER_ZONE_SIZE,
      width: EDGE_ZONE_THICKNESS,
    },
  },
  {
    zone: 'w',
    cursor: 'ew-resize',
    z: 20,
    style: {
      left: -HALF_EDGE,
      top: CORNER_ZONE_SIZE,
      bottom: CORNER_ZONE_SIZE,
      width: EDGE_ZONE_THICKNESS,
    },
  },
  // ── 隅正方形 (z=21, 辺より手前) ──
  {
    zone: 'nw',
    cursor: 'nwse-resize',
    z: 21,
    style: { top: -HALF_EDGE, left: -HALF_EDGE, width: CORNER_ZONE_SIZE, height: CORNER_ZONE_SIZE },
  },
  {
    zone: 'ne',
    cursor: 'nesw-resize',
    z: 21,
    style: {
      top: -HALF_EDGE,
      right: -HALF_EDGE,
      width: CORNER_ZONE_SIZE,
      height: CORNER_ZONE_SIZE,
    },
  },
  {
    zone: 'sw',
    cursor: 'nesw-resize',
    z: 21,
    style: {
      bottom: -HALF_EDGE,
      left: -HALF_EDGE,
      width: CORNER_ZONE_SIZE,
      height: CORNER_ZONE_SIZE,
    },
  },
  {
    zone: 'se',
    cursor: 'nwse-resize',
    z: 21,
    style: {
      bottom: -HALF_EDGE,
      right: -HALF_EDGE,
      width: CORNER_ZONE_SIZE,
      height: CORNER_ZONE_SIZE,
    },
  },
];

export const PreviewOverlay = ({
  canvasRef,
  wrapperRef,
  viewMapper,
  onSetViewport,
  canvasWidth,
  canvasHeight,
  zoom,
  flipX,
  onClose,
  initialEdge = DEFAULT_PREVIEW_EDGE,
  onResizeCommit,
  isSuspended,
  backgroundVisible,
  backgroundRgb,
}: PreviewOverlayProps): React.ReactNode => {
  // サムネ最大辺 (px) の **ユーザ希望値 (= 生値)**. 右下グリップのドラッグで
  // 連続的に変化する. 初期値は caller が渡す永続値 (= store.get()) で seed.
  // リサイズ確定時に onResizeCommit(previewEdge) で永続化する (preview-resize.md §3).
  // 表示時は viewport の実効 max で clamp する (= displayEdge, 下記) ため、ここには
  // 生値 (最大 640) を保持し、小画面で縮めても大画面に戻れば希望サイズへ復帰する.
  const [previewEdge, setPreviewEdge] = useState<number>(initialEdge);

  // ─── viewport 追従 (= 動的 maxEdge cap, feature/preview-size-viewport-cap) ───
  // window innerWidth/innerHeight を state に持ち、resize で更新する. これを基に
  // 「窓が画面に収まる実効上限 (effectiveMaxEdge)」を算出して、ドラッグリサイズの
  // clamp と表示適用の clamp に使う. 小画面で 640 まで広げて画面外にはみ出すのを防ぐ.
  const [viewport, setViewport] = useState<{ width: number; height: number }>(() =>
    typeof window === 'undefined'
      ? { width: 1024, height: 768 }
      : { width: window.innerWidth, height: window.innerHeight },
  );
  // viewport に収まる実効上限 (px). 大画面では従来どおり 640、小画面でだけ下がる.
  const effectiveMax = useMemo(
    () => effectivePreviewMaxEdge(viewport.width, viewport.height),
    [viewport],
  );
  // 実際に表示/適用する edge = 生値を実効 max で clamp. window 縮小時はここが自動で
  // 下がり (= 窓が画面に収まる)、大画面に戻れば previewEdge (生値) まで復元する.
  const displayEdge = clampPreviewEdgeToViewport(previewEdge, viewport.width, viewport.height);

  // useMemo で thumb を安定化させて useEffect の deps を `[thumb, ...]` で
  // 表現できるようにする (biome `useExhaustiveDependencies` 対応).
  // canvasWidth/canvasHeight/displayEdge が変わったときだけ再計算.
  const thumb = useMemo(
    () => computePreviewThumbnailSize(canvasWidth, canvasHeight, displayEdge),
    [canvasWidth, canvasHeight, displayEdge],
  );
  const thumbCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  // 枠ドラッグ (8 方向) resize のアンカー. pointerdown 時に「掴んだゾーン・
  // origin edge・pointer 起点・origin popupPos・origin パネル実寸・chrome
  // (= パネル実寸 − thumb 実寸)」を記録し、pointermove で nextPreviewResize に
  // 流す. null = 非リサイズ. chrome は edge に依らず一定とみなす (= title bar +
  // padding 分は thumb サイズが変わっても不変).
  const resizeAnchorRef = useRef<{
    zone: PreviewResizeZone;
    originEdge: number;
    pointerStart: { x: number; y: number };
    originPos: FloatingPopupPosition;
    originSize: { w: number; h: number };
    chrome: { w: number; h: number };
  } | null>(null);

  // 2026-06-13 (feature/preview-perf): サムネ + 表示範囲枠の rAF ループを Classic/Modern
  // 共有フックへ括り出した (= 二重実装の解消)。サムネ再生成は低レートスロットル + 自分の
  // 操作中サスペンド、表示範囲枠は分離して安く追従する (詳細は usePreviewThumbnailLoop)。
  usePreviewThumbnailLoop({
    thumbCanvasRef,
    canvasRef,
    wrapperRef,
    viewMapper,
    thumb,
    isSuspended,
  });

  // ─── ESC で閉じる (ColorWheelPalette と同じパターン). ──────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // ─── 初期位置: 画面右下 (= 旧版 PreviewFrame は dialog で位置自由だったが、
  //     新版は ColorWheelPalette に寄せて画面右側に固定). SSR では 24,24 fallback.
  const [popupPos, setPopupPos] = useState<FloatingPopupPosition>(() => {
    if (typeof window === 'undefined') return { x: 24, y: 24 };
    const PANEL_WIDTH = thumb.width + 16; // padding 込みのおおよそ
    const RIGHT_MARGIN = 16;
    const BOTTOM_MARGIN = 16;
    const PANEL_HEIGHT_APPROX = thumb.height + 40; // title bar + body
    return {
      x: Math.max(16, window.innerWidth - PANEL_WIDTH - RIGHT_MARGIN),
      y: Math.max(16, window.innerHeight - PANEL_HEIGHT_APPROX - BOTTOM_MARGIN),
    };
  });
  const dragAnchorRef = useRef<FloatingDragAnchor | null>(null);
  // root 要素 ref. ドラッグ / resize clamp で実寸 (getBoundingClientRect) を読む.
  const panelRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();

  // 実寸を読む helper. ref 未取得時は thumb 寸法から概算した fallback.
  // fallback が thumb に依存するので useCallback([thumb]) で安定化して effect
  // deps に渡す (= thumb 変化時のみ作り直す, panelRef は ref なので deps 不要).
  const measurePanel = useCallback((): { width: number; height: number } => {
    const el = panelRef.current;
    if (el !== null) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return { width: r.width, height: r.height };
    }
    // title bar + body padding を含むおおよそ (= 初期位置算出と同じ概算).
    return { width: thumb.width + 16, height: thumb.height + 40 };
  }, [thumb]);

  // ─── 四隅 resize 確定 (pointerup / cancel 共通). ─────────────────────────
  // 確定時に 1 回だけ永続化 (= move 毎は localStorage 過剰書き込み) + 大きくして
  // 画面外にはみ出した分を panel ごと画面内へ引き戻す. previewEdge は state
  // closure 越しに読むので useCallback deps に含める.
  const commitResize = useCallback((): void => {
    onResizeCommit?.(previewEdge);
    if (typeof window !== 'undefined') {
      setPopupPos((cur) =>
        clampPopupPosition(cur, measurePanel(), {
          width: window.innerWidth,
          height: window.innerHeight,
        }),
      );
    }
  }, [onResizeCommit, previewEdge, measurePanel]);

  // ─── window resize 追従: viewport state を更新する ───────────────────────
  // viewport が縮むと effectiveMax → displayEdge → thumb が縮み、下の clamp 効果が
  // 画面内へ位置を引き戻す. サイズ自体を実効 max まで縮める (= 従来は位置のみ
  // 引き戻していたが、640 のままはみ出していた) のがこの追従の目的.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = (): void => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ─── thumb / viewport が変わったら画面外に取り残された窓を引き戻す ─────────
  // window 縮小で thumb が縮んだ後 (= 再 render 後) に measurePanel が新しい実寸を
  // 返すので、この効果を measurePanel (= thumb 変化で作り直される useCallback) と
  // viewport に依存させ、再 render 後に clamp する. clamp は冪等 (= 収まっていれば
  // 同値) なので無限ループにはならない.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setPopupPos((cur) => clampPopupPosition(cur, measurePanel(), viewport));
  }, [viewport, measurePanel]);

  // ─── サムネ click/drag → viewport pan. setPointerCapture でドラッグ追従. ──
  const moveViewportToClient = (clientX: number, clientY: number): void => {
    const thumbCanvas = thumbCanvasRef.current;
    const wrapper = wrapperRef.current;
    if (thumbCanvas === null || wrapper === null) return;
    const r = thumbCanvas.getBoundingClientRect();
    const xThumb = clientX - r.left;
    const yThumb = clientY - r.top;
    const t = viewMapper.getTransform();
    if (t.rotation !== 0) {
      // 回転中: サムネ click → 真の canvas 座標 → 枠中央へ (回転対応 clamp)。
      const slot = measureSlot(wrapper);
      onSetViewport(
        previewClickToViewportRotated(thumb, xThumb, yThumb, t, slot.slotWidth, slot.slotHeight),
      );
      return;
    }
    const next = previewClickToViewport(
      thumb,
      xThumb,
      yThumb,
      wrapper.clientWidth,
      wrapper.clientHeight,
      zoom,
      flipX,
      canvasWidth,
      canvasHeight,
    );
    onSetViewport(next);
  };

  // 2026-09-28 (feature/preview-panel-background): サムネ canvas にもメインと同じ
  // CSS 背景を当てる (DrawingCanvas と同じ helper、= 二重実装を避ける)。
  const bg = canvasBackgroundStyle(backgroundVisible, backgroundRgb);

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="プレビュー"
      aria-labelledby={titleId}
      className={`fixed z-40 bg-[var(--color-panel)] text-white ${outsetFrame}`}
      style={{ left: popupPos.x, top: popupPos.y, width: thumb.width + 16 }}
    >
      {/* タイトルバー = drag handle. ColorWheelPalette と同じパターン.
          ペンタブ対策: `touchAction: 'none'` で OS の pan / scroll ジェスチャ解釈を
          抑止する (= 解釈されるとブラウザが pointercancel を飛ばしてドラッグが
          途切れたり、capture 確立前にジッタが入ったりする). */}
      <div
        className="flex cursor-move select-none items-center justify-between border-b border-black bg-[var(--color-bg-soft)] px-2 py-1 text-[10px] font-bold text-white"
        style={{ touchAction: 'none' }}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          dragAnchorRef.current = {
            origin: popupPos,
            pointerStart: { x: e.clientX, y: e.clientY },
          };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const anchor = dragAnchorRef.current;
          if (anchor === null) return;
          // 算出位置を viewport 内に clamp してから set (= 画面外に出せない).
          const next = nextPositionFromDrag(anchor, { x: e.clientX, y: e.clientY });
          setPopupPos(
            clampPopupPosition(next, measurePanel(), {
              width: window.innerWidth,
              height: window.innerHeight,
            }),
          );
        }}
        onPointerUp={(e) => {
          dragAnchorRef.current = null;
          try {
            e.currentTarget.releasePointerCapture(e.pointerId);
          } catch {
            // 既に release 済の defensive (Safari 等で起きうる).
          }
        }}
      >
        <span id={titleId}>プレビュー</span>
        <button
          type="button"
          aria-label="プレビューを閉じる"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onClose}
          className="border border-black bg-white px-2 py-0 text-[10px] font-bold text-black"
        >
          ✕
        </button>
      </div>
      {/* 本体. サムネ canvas は thumb 寸法ぴったり (= CSS px = drawing buffer px). */}
      <div className="flex justify-center p-2">
        <canvas
          ref={thumbCanvasRef}
          // backing store (width/height 属性) は rAF ループ内で dpr 倍に設定
          // する (= DPR 対応). ここで CSS サイズを明示しておかないと、backing
          // を物理ピクセルに広げた瞬間に要素が dpr 倍の見た目で表示されて
          // しまうため、style で論理 px (thumb.width/height) に固定する.
          // 背景 (`bg.style`) は DrawingCanvas と同じ CSS 方式 (2026-09-28、
          // `canvasBackgroundStyle` 共有 helper): 可視時は backgroundColor、非可視時は
          // 下の className の `canvas-bg-checker` に任せる (= 画素へ焼き込まない).
          style={{
            touchAction: 'none',
            cursor: 'crosshair',
            width: thumb.width,
            height: thumb.height,
            ...bg.style,
          }}
          className={bg.extraClassName ?? undefined}
          aria-label="キャンバス全体プレビュー (クリックで表示位置移動)"
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            e.preventDefault();
            isDraggingRef.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
            moveViewportToClient(e.clientX, e.clientY);
          }}
          onPointerMove={(e) => {
            if (!isDraggingRef.current) return;
            moveViewportToClient(e.clientX, e.clientY);
          }}
          onPointerUp={(e) => {
            isDraggingRef.current = false;
            try {
              e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
              // defensive
            }
          }}
          onPointerCancel={(e) => {
            isDraggingRef.current = false;
            try {
              e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
              // defensive
            }
          }}
        />
      </div>
      {/* 枠ドラッグ (Windows 風 8 方向) リサイズゾーン (H follow-up #2).
          - 見えるグリップは撤去. パネルの縁に乗る透明な判定 div を 8 個
            (4 辺 + 4 隅) 置き、ホバーで各方向の resize カーソルだけ出す.
          - サムネ canvas の click→scroll / タイトルバー drag-move とは別 DOM +
            `stopPropagation`/`preventDefault` で当たり判定を分離 (= n/ne/nw は
            title bar と重なるが、これらを title bar より手前 (z 高) に置くこと
            で「最上端の細い帯 = リサイズ、その下のバー本体 = 移動」になる).
          - 掴んだ辺/隅の反対側を固定点にして `nextPreviewResize` が edge +
            popupPos を同時に更新 (= アスペクト固定で掴んだ側だけが動く). 確定時
            (up/cancel) に onResizeCommit で永続化 + panel を画面内へ再 clamp. */}
      {PREVIEW_ZONES.map(({ zone, cursor, z, style }) => (
        <div
          key={zone}
          aria-hidden="true"
          title="ドラッグでプレビューのサイズを変更"
          className="absolute"
          style={{ ...style, zIndex: z, cursor, touchAction: 'none' }}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            // サムネ click→scroll / タイトルバー drag-move と干渉させない.
            e.stopPropagation();
            e.preventDefault();
            const size = measurePanel();
            resizeAnchorRef.current = {
              // origin は **表示中の edge** (= displayEdge). 生値 previewEdge は
              // 小画面で displayEdge より大きいことがあり、それを origin にすると
              // originSize (実測 = displayEdge ベース) と食い違ってドラッグが
              // ジャンプするため、見えている displayEdge を起点にする.
              zone,
              originEdge: displayEdge,
              pointerStart: { x: e.clientX, y: e.clientY },
              originPos: popupPos,
              originSize: { w: size.width, h: size.height },
              // chrome = パネル実寸 − thumb 実寸 (= title bar + padding 分).
              chrome: { w: size.width - thumb.width, h: size.height - thumb.height },
            };
            // ゾーン外へドラッグが出ても追従させる (= OS ウィンドウ枠と同様).
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            const anchor = resizeAnchorRef.current;
            if (anchor === null) return;
            e.stopPropagation();
            const res = nextPreviewResize({
              zone: anchor.zone,
              originEdge: anchor.originEdge,
              pointerStart: anchor.pointerStart,
              pointer: { x: e.clientX, y: e.clientY },
              originPos: anchor.originPos,
              originSize: anchor.originSize,
              canvasWidth,
              canvasHeight,
              chrome: anchor.chrome,
              // 固定 640 でなく viewport 由来の実効 max で上限 clamp (= 小画面では
              // 640 未満で止まり、窓が画面からはみ出さない).
              max: effectiveMax,
            });
            setPreviewEdge(res.edge);
            setPopupPos({ x: res.x, y: res.y });
          }}
          onPointerUp={(e) => {
            if (resizeAnchorRef.current === null) return;
            e.stopPropagation();
            resizeAnchorRef.current = null;
            try {
              e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
              // 既に release 済の defensive (Safari 等で起きうる).
            }
            commitResize();
          }}
          onPointerCancel={(e) => {
            if (resizeAnchorRef.current === null) return;
            e.stopPropagation();
            resizeAnchorRef.current = null;
            try {
              e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {
              // defensive
            }
            commitResize();
          }}
        />
      ))}
    </div>
  );
};
